import { lazy } from "react";
import { createErrorMessage } from "@aviv-unified-frontend/internal";
import { getRemoteApplication } from "../utils/getRemoteApplication";
export { getRemoteApplication } from "../utils/getRemoteApplication";
/**
 * Lazy load a remote component.
 * For React best practices, follow {@link https://react.dev/reference/react/lazy#troubleshooting | this documentation}.
 */
export const getLazyRemoteApplicationComponent = (id, url) => {
    return lazy(async () => ({
        default: getRemoteApplication(await loadRemoteApplicationDynamically(id, url)).Component,
    }));
};
export const getRemoteApplicationDynamically = async (id, url) => getRemoteApplication(await loadRemoteApplicationDynamically(id, url));
/**
 * Dynamic Federation
 * https://github.com/module-federation/module-federation-examples/blob/b4d4ae6dc4a97808ca637ecb4e5a80d09916dfe5/redux-reducer-injection/app1/src/bootstrap.js#L7
 */
const loadRemoteApplicationDynamically = (id, url) => new Promise((resolve, reject) => {
    const onLoad = async () => {
        const container = window[id];
        if (!container) {
            reject(new Error(createErrorMessage({
                cause: `The \`${id}\` container cannot be found`,
                solution: "Make sure the container is correctly exported and available in the global scope",
            })));
            return;
        }
        if (!__webpack_share_scopes__) {
            reject(new Error(createErrorMessage({
                cause: `The __webpack_share_scopes__ variable is not defined for application \`${id}\``,
                solution: "Make sure the variable is correctly exported and available in the global scope",
            })));
            return;
        }
        await container.init(__webpack_share_scopes__.default);
        const factory = await container.get(".");
        resolve({ id, exports: factory() });
    };
    if (window[id]) {
        onLoad();
        return;
    }
    let script = document.head.querySelector(`script[src="${url}"]`);
    if (script?.dataset.error) {
        reject(new Error(`Failed to load remote application ${id}`));
        return;
    }
    if (script) {
        script.onload = onLoad;
        script.onerror = reject;
        return;
    }
    script = document.createElement("script");
    script.src = url;
    script.defer = true;
    script.onload = onLoad;
    script.onerror = reject;
    document.head.appendChild(script);
});
